import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { History, Location } from 'history';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { faArchive } from '@fortawesome/free-solid-svg-icons';
import { INVITE_KIND, INVITE_TABS, INVITE_TYPE } from 'Src/consts/invite';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getEventDataToInvite,
  getEventVenueInviteArchive,
  getInviteStatus,
  getSenderNameIfNeedInviteArchive,
  getStartDateFromInvite
} from 'Src/helpers/accessor/accessor';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { AdminSchoolsEventSchoolInviteSummary } from 'Src/components/AdminSchoolInviteSummary/AdminSchoolsEventSchoolInviteSummary';
import {
  getAdminSchoolsAllArchiveInvites,
  searchAdminSchoolInvites
} from 'Src/helpers/service/adminSU/schoolInvitesTable';
import { AdminSchoolsTournamentSchoolInviteSummary } from 'Src/components/AdminSchoolInviteSummary/AdminSchoolsTournamentSchoolInviteSummary';
import { SchoolInvite } from 'Src/models/invite';
import { InviteDetailsDrawer } from './InviteDetailsDrawer';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Event',
    field: 'event',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getEventDataToInvite
  },
  {
    text: 'School',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getSenderNameIfNeedInviteArchive
  },
  {
    text: 'Start time',
    field: 'startTime',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getStartDateFromInvite
  },
  {
    text: 'Venue',
    field: 'venue',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getEventVenueInviteArchive
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.NONE,
    cell: invite => {
      const status = getInviteStatus(invite);
      return (
        <td key="status">
          <span
            className={`schoolInvitesStatus schoolInvitesStatus--${String(status)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')}`}
          >
            {status}
          </span>
        </td>
      );
    }
  },
  {
    text: '',
    field: 'open',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: () => (
      <td key="open" className="schoolInvitesList__openCell">
        <span aria-hidden="true">›</span>
      </td>
    )
  }
];

interface State {
  items: SchoolInvite[];
  currentPage: number;
  selectedItems: SchoolInvite[];
  viewedInvite: SchoolInvite | null;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  userTags: any[];
  total: number;
  basePath: string;

  currentTabIndex: number;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class AdminSchoolsInvitesArchive extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      viewedInvite: null,
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      userTags: [],
      total: 0,
      basePath: '',

      currentTabIndex: 0
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('mousedown', this.onFilterOutsideClick);
    this.setItems();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onFilterOutsideClick);
  }

  onFilterOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (
      !this.state.isShowFilter ||
      (target.closest &&
        (target.closest('.gridModernFilterPopover') ||
          target.closest('.gridModernFiltersAnchor') ||
          target.closest('.sit-dp__pop') ||
          target.closest('.sit-cal') ||
          target.closest('[role="dialog"]')))
    ) {
      return;
    }
    this.setState({ isShowFilter: false });
    this.setItems();
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps.location.search);
    }
  }

  setItems(locationSearchOverride?: string) {
    const { history, user } = this.props;

    const search = parse(typeof locationSearchOverride === 'string' ? locationSearchOverride : history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);

    const dateRange = filters.startTime || {};
    const schoolQuery = filters.name || '';

    const searchPromise = searchAdminSchoolInvites(user, 'archive', {
      page,
      schoolQuery,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
      sortField: sortColumnsName || 'startTime',
      sortDirection: sortDirection || 'DESC'
    });
    const getItemsPromise = searchPromise.then(result => result.items);
    const getItemsCountPromise = searchPromise.then(result => ({ count: result.count }));

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      /** We need the activeSchoolId in the accessor to specify the correct venue */
      const itemsWithAdditionActiveSchoolId = items.map(element => {
        return {
          ...element,
          activeSchoolId: user.activeSchoolId
        };
      });

      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: itemsWithAdditionActiveSchoolId,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    this.setState({ viewedInvite: this.state.items[index], currentTabIndex: 0 });
  };

  onItemSelect = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters, { isCorrectTime: true });

    getAdminSchoolsAllArchiveInvites(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernRange) {
      nextFilters = { ...nextFilters, [filterField]: options.modernRange };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      filters: {},
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false });
    this.setItems();
  };

  renderInviteTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total
    } = this.state;

    const actionItems = [];

    const filterOptions = {};

    return (
      <Grid2
        gridTitle="Fixture invite archive"
        gridTitleCount={total}
        isActionsInline={true}
        isCSV={false}
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        user={this.props.user}
        columnVisibilityKey="sports-fixtures-invites-archive"
        fixedColumnFields={['event', 'open']}
        hiddenColumnToggleFields={['open']}
        toolbarSearchPlaceholder="Search by school…"
        isSelectionHidden={true}
        emptyText="No archived invitations"
        emptyIcon={faArchive}
        emptyHint="Completed and expired fixture or tournament invitations will appear here."
        emptyFilteredText="No archived invitations match your filters"
        emptyFilteredHint="Try changing or clearing the search and filters to find an archived invitation."
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onItemSelect={this.onItemSelect}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  renderSummary(invite?: SchoolInvite): React.ReactNode {
    const summaryInvite = invite || this.state.selectedItems[0];
    if (!summaryInvite) {
      return <div className="font-weight-bold">Select a invite above</div>;
    }

    const { currentTabIndex } = this.state;
    const summaryTabs = this.getSummaryTabs(summaryInvite);

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={INVITE_TABS} currentTabIndex={currentTabIndex}>
        {summaryTabs}
      </GenericSummary>
    );
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  getSummaryTabs(invite: SchoolInvite = this.state.selectedItems[0]): React.ReactNode {
    const { activeSchoolId } = this.props.user;

    const { currentTabIndex } = this.state;

    const messageType = INVITE_TYPE.ARCHIVE;

    switch (currentTabIndex) {
      case 0:
        switch (true) {
          case invite.kind === INVITE_KIND.EVENT_SCHOOL_INVITE:
            return (
              <AdminSchoolsEventSchoolInviteSummary
                invite={invite}
                activeSchoolId={activeSchoolId}
                messageType={messageType}
              />
            );

          case invite.kind === INVITE_KIND.TOURNAMENT_SCHOOL_INVITE:
            return (
              <AdminSchoolsTournamentSchoolInviteSummary
                invite={invite}
                activeSchoolId={activeSchoolId}
                messageType={messageType}
              />
            );
        }
    }
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = 'mt-3';

    return (
      <div className={`${classes} schoolInvitesList`}>
        {/*Place for render modal windows*/}
        <div className="row">
          <div className="col-md-12">
            {this.renderInviteTable()}
            {this.state.viewedInvite && (
              <InviteDetailsDrawer
                eventName={getEventDataToInvite(this.state.viewedInvite)}
                date={getStartDateFromInvite(this.state.viewedInvite)}
                schoolName={getSenderNameIfNeedInviteArchive(this.state.viewedInvite, { user: this.props.user })}
                venue={getEventVenueInviteArchive(this.state.viewedInvite, { user: this.props.user })}
                status={getInviteStatus(this.state.viewedInvite)}
                folder="Fixture invite archive"
                onClose={() => this.setState({ viewedInvite: null })}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
